<template>
  <!-- This component exists with just the annotations, seats,
  and sections so that we can avoid a re-render of all these elements
  when we're only changing properties of the container (primarily zoom).
  In certain cases though, we may need to re-render the component; if so,
  we can simply toggle the ref field 'refresher', which will force it to re-render -->
  <div :class="refresher">
    <div
      v-for="annotation in context.seatMap.annotations"
      :key="`${annotation.id}-${annotation.label}`"
    >
      <div
        v-if="annotation.shape != null"
        class="drawable annotation"
        :class="{
          active: context.selectedObjects.has(annotation),
          ['shape-' + SeatMapAnnotationShape[annotation.shape]]: true,
        }"
        :style="{
          top: annotation.y + 'px',
          left: annotation.x + 'px',
          width: annotation.width + 'px',
          height: annotation.height + 'px',
        }"
        @mousedown="context.onObjectMouseDown($event, annotation)"
        @click="context.onObjectClick($event, annotation)"
      >
        <span
          class="annotation-label"
          :style="{ 'font-size': annotation.fontScale + 'em' }"
          v-text="annotation.label"
        >
        </span>

        <div
          v-if="
            context.selectedObjects.has(annotation) &&
            context.selectedObjects.size == 1
          "
          class="resize-handle"
        ></div>
      </div>
    </div>

    <div
      v-for="(section, sectionIndex) in context.seatMap.sections"
      :key="'section-' + sectionIndex"
      :style="{ '--section-color': section.color }"
      :class="{ 'is-hidden-section': section.isHidden }"
    >
      <template v-if="!section.isHidden || context.showHiddenSections">
        <div
          v-for="(seat, seatIndex) in section.seats"
          :key="'seat-' + sectionIndex + '-' + seatIndex"
          class="drawable seat"
          :class="context.getSeatClasses(seat, section).classes"
          :style="{
            top: seat.y + 'px',
            left: seat.x + 'px',
            width: seat.width ? seat.width + 'px' : '',
            height: seat.height ? seat.height + 'px' : '',
          }"
          :title="
            (!seat.seatNumber?.includes(GeneralAdmissionSeatNumber.Prefix)
              ? context.getSeatClasses(seat, section).purchaserName
              : null) ??
            context.getSeatClasses(seat, section).agentNote ??
            seat.seatNumber ??
            undefined
          "
          :aria-label="getSeatAriaLabel(seat, section)"
          :role="
            isSeatAvailable(seat, section) || isTakenWithInfo(seat, section)
              ? 'button'
              : 'img'
          "
          :tabindex="
            isSeatAvailable(seat, section) || isTakenWithInfo(seat, section)
              ? '0'
              : '-1'
          "
          @mousedown="
            isSeatAvailable(seat, section) &&
            context.onObjectMouseDown($event, seat)
          "
          @click="
            (isSeatAvailable(seat, section) ||
              isTakenWithInfo(seat, section)) &&
            context.onObjectClick($event, seat)
          "
        >
          <span
            class="seat-text"
            :style="{
              fontSize: seat.seatNumber?.includes(
                GeneralAdmissionSeatNumber.Prefix,
              )
                ? seat.fontScale + 'em'
                : 1 - 0.14 * ((seat.seatNumber?.length ?? 2) - 2) + 'em',
            }"
            aria-hidden="true"
          >
            {{ seat.seatNumber }}
          </span>
          <div
            v-if="
              context.selectedObjects.has(seat) &&
              context.selectedObjects.size == 1 &&
              seat.seatNumber?.includes(GeneralAdmissionSeatNumber.Prefix)
            "
            class="resize-GA-handle"
          ></div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.seat-map-drawing {
  position: relative;
  overflow: auto;
  max-width: 100%;

  transform-origin: top left;

  .drawable {
    box-sizing: border-box;
    position: absolute;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #333;

    &:hover {
      &::before {
        opacity: 0.08;
        background-color: var(--section-color);
        bottom: 0;
        border-radius: inherit;
        content: "";
        left: 0;
        position: absolute;
        pointer-events: none;
        right: 0;
        top: 0;
        z-index: 30;
      }
    }
  }

  .annotation {
    --section-color: #333;
    border: 2px solid var(--section-color);

    &.active {
      background: var(--section-color);
      color: white;
    }

    &.shape-RoundedRectangle {
      border-radius: 15px;
    }

    &.shape-Oval {
      border-radius: 50%;
    }

    &.shape-None {
      border-color: transparent;

      &.active {
        background: transparent;
        color: inherit;
      }
    }

    .annotation-label {
      user-select: none;
      white-space: pre-wrap;
    }

    .resize-handle {
      $width: 8px;
      width: $width;
      height: $width;
      background: white;
      border: 1px solid var(--section-color);
      cursor: nwse-resize;
      bottom: -$width / 2;
      right: -$width / 2;
      position: absolute;
      z-index: 100;
    }
  }

  .gaa {
    border-radius: 15px;
    border: 2px solid var(--section-color);

    .resize-GA-handle {
      $width: 8px;
      width: $width;
      height: $width;
      background: white;
      border: 1px solid var(--section-color);
      cursor: nwse-resize;
      bottom: -$width / 2;
      right: -$width / 2;
      position: absolute;
      z-index: 100;
    }

    &.active {
      background: var(--section-color);
      color: white;
    }

    &.taken {
      $takenColor: #aaa;
      cursor: not-allowed;
      color: $takenColor;
      --section-color: #{$takenColor};

      .seat-text {
        display: none;
      }

      &::after {
        content: "\f007"; //fa-user
        font-family: "Font Awesome 5 Pro";
        font-weight: 300;
        color: var(--section-color);
      }
    }

    &.blocked {
      $blockedColor: #dc3545; // Red color for blocked seats
      cursor: not-allowed;
      color: white;
      --section-color: #{$blockedColor};
      background: $blockedColor;

      .seat-text {
        display: none;
      }

      &::after {
        content: "\f023"; //fa-lock
        font-family: "Font Awesome 5 Pro";
        font-weight: 300;
        color: white;
      }
    }
    &.checked {
      border-width: 4px;

      .seat-text {
        display: none;
      }

      &::after {
        content: "\f00c"; //fa-check
        font-family: "Font Awesome 5 Pro";
        font-weight: 600;
        color: var(--section-color);
      }
    }
  }

  .seat {
    $size: 30px;

    width: $size;
    height: $size;

    border-radius: $size/2;
    border: 2px solid var(--section-color);
    transition: box-shadow ease 0.2s;

    .seat-text {
      user-select: none;
      white-space: nowrap;
    }

    &.active {
      background: var(--section-color);
      color: white;
    }

    &.taken {
      $takenColor: #aaa;
      cursor: not-allowed;
      color: $takenColor;
      --section-color: #{$takenColor};

      .seat-text {
        display: none;
      }

      &::after {
        content: "\f007"; //fa-user
        font-family: "Font Awesome 5 Pro";
        font-weight: 300;
        color: var(--section-color);
      }
    }

    &.blocked {
      $blockedColor: #aaa; // Red color for blocked seats
      cursor: not-allowed;
      color: #aaa;
      --section-color: #{$blockedColor};
      background: white;

      .seat-text {
        display: none;
      }

      &::after {
        content: "\f023"; //fa-lock
        font-family: "Font Awesome 5 Pro";
        font-weight: 300;
        color: #aaa;
      }
    }

    &.taken-with-info {
      $takenColor: #aaa;
      cursor: pointer;
      color: $takenColor;
      --section-color: #{$takenColor};

      .seat-text {
        display: none;
      }

      &::after {
        content: "\f007"; //fa-user
        font-family: "Font Awesome 5 Pro";
        font-weight: 300;
        color: var(--section-color);
      }
    }

    &.checked {
      border-width: 4px;

      .seat-text {
        display: none;
      }

      &::after {
        content: "\f00c"; //fa-check
        font-family: "Font Awesome 5 Pro";
        font-weight: 600;
        color: var(--section-color);
      }
    }
  }

  &.hide-seat-text .seat-text {
    display: none;
  }

  .is-hidden-section {
    .seat,
    .gaa {
      border-style: dashed;
      opacity: 0.7;

      &.active {
        opacity: 1;
        background-color: color-mix(
          in srgb,
          var(--section-color),
          transparent 50%
        ) !important;
        border-color: var(--section-color) !important;
        border-style: dashed !important;
      }
    }
  }
}
</style>

<script setup lang="ts">
import { SeatMapAnnotationShape } from "@/models.g";
import { SeatMapContext } from "@common/seat-map-context";
import { GeneralAdmissionSeatNumber } from "../../app-private/consts";
import { SeatMapSeatDto, SeatMapSectionDto } from "@/models.g";
import { eventBus, EventTypes } from "../../eventBus";

const props = defineProps<{
  context: SeatMapContext;
}>();

/**
 * (*) This ref is used to explicitly refresh the seatmap drawing,
 * when toggled
 */
const refresher = ref(false);

// Subscribing to RefreshSeatMap event, which is sent when the context needs this component to re-render itself
onMounted(() => {
  eventBus.on(EventTypes.RefreshSeatMap, refreshSeatMap);
});

onUnmounted(() => {
  eventBus.off(EventTypes.RefreshSeatMap, refreshSeatMap);
});
//

/**
 * Refreshes this component by changing
 * the value of the ref field (*)
 */
function refreshSeatMap() {
  refresher.value = !refresher.value;
}

function getSeatAriaLabel(
  seat: SeatMapSeatDto,
  section: SeatMapSectionDto,
): string {
  const classes = props.context.getSeatClasses(seat, section).classes as any;
  const seatNumber = seat.seatNumber || "Unknown seat";

  // Check if seat is checked
  if (isSeatChecked(classes)) {
    return `Seat ${seatNumber}, checked, click to deselect`;
  }

  // Check if seat is taken but info is available (agent/admin view)
  if (classes["taken-with-info"]) {
    return `Seat ${seatNumber}, sold, click to view purchaser details`;
  }

  // Check if seat is locked/unavailable
  if (isSeatLocked(classes)) {
    return `Seat ${seatNumber}, locked, unavailable`;
  }

  // Check if seat is reserved
  if (isSeatReserved(classes)) {
    return `Seat ${seatNumber}, reserved, unavailable`;
  }

  // Available seat
  return `Seat ${seatNumber}, available, click to select`;
}

function isSeatLocked(classes: any): boolean {
  // Check the seat classes or status to determine if locked
  return isSeatReserved(classes) || classes.blocked;
}
function isSeatChecked(classes: any): boolean {
  return classes.checked;
}

function isSeatReserved(classes: any): boolean {
  return classes.taken || classes["taken-with-info"];
}

function isTakenWithInfo(
  seat: SeatMapSeatDto,
  section: SeatMapSectionDto,
): boolean {
  const classes = props.context.getSeatClasses(seat, section).classes as any;
  return !!classes["taken-with-info"];
}

function isSeatAvailable(
  seat: SeatMapSeatDto,
  section: SeatMapSectionDto,
): boolean {
  const classes = props.context.getSeatClasses(seat, section).classes as any;
  return !isSeatLocked(classes) && !isSeatReserved(classes);
}
</script>
